<template>
  <a-modal :visible="visible" width="800px" title="Create Company" @cancel="closeModal" v-if="visible">
    <a-form-model layout="vertical" ref="form" class="flex gap-4 text-left" :model="form" :rules="rules">
      <div class="flex flex-column gap-2">
        <upload-logo :height="128" :width="128" @upload="logo_file_id = $event" />

        <a-form-model-item :colon="false" label-align="left" label="Name" prop="name">
          <a-input v-model="form.name" placeholder="Name" autocomplete="off" />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Short Name" prop="short_name">
          <a-input v-model="form.short_name" placeholder="Short Name" autocomplete="off" />
        </a-form-model-item>
        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Join Type"
          prop="join_type"
          :extra="joinTypesList.find(item => item.id === form.join_type)?.extra"
        >
          <custom-select
            :multiple="false"
            :allowClear="false"
            :value.sync="form.join_type"
            placeholder="Join Type"
            :options="joinTypesList"
            name="name"
            optionKey="id"
          />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Tax ID (EIN)" prop="tax_id" :help="taxIdExtraText">
          <a-input v-model="form.tax_id" :masked="true" raw v-mask="'##-#######'" placeholder="Tax ID (EIN)"
            ><a-icon v-show="loadingTaxId || loadingProject" slot="suffix" type="loading"
          /></a-input> </a-form-model-item
        ><a-form-model-item :colon="false" label-align="left" label="Payroll Tax ID" prop="payroll_tax_id">
          <a-input
            v-model="form.payroll_tax_id"
            :masked="true"
            raw
            v-mask="'##-#######'"
            placeholder="Payroll Tax ID"
          />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Time Zone" prop="time_zone">
          <custom-select
            :multiple="false"
            :allowClear="false"
            :value.sync="form.time_zone"
            placeholder="Time Zone"
            :options="timeZones"
            name="name"
            optionKey="id"
          />
        </a-form-model-item>
      </div>

      <h3>Address</h3>

      <div class="flex flex-column gap-2">
        <a-form-model-item :colon="false" label-align="left" label="Address Line 1" prop="address.address_line1">
          <a-input v-model="form.address.address_line1" placeholder="Enter Address" />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Address Line 2" prop="address.address_line2">
          <a-input v-model="form.address.address_line2" placeholder="Enter Address" />
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="City" prop="address.city">
          <a-input v-model="form.address.city" placeholder="Enter City" />
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="Country" prop="address.country">
          <custom-select
            :multiple="false"
            :value.sync="form.address.country"
            placeholder="Enter Country"
            :options="countriesList"
            name="name"
            optionKey="code"
          />
        </a-form-model-item>

        <a-form-model-item
          v-if="form.address.country === 'US' || form.address.country === 'us'"
          :colon="false"
          label-align="left"
          label="State"
          prop="address.state"
        >
          <custom-select
            :multiple="false"
            :value.sync="stateProxy"
            placeholder="Enter State"
            :options="statesList"
            name="name"
            optionKey="code"
          />
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="Zipcode" prop="address.zipcode">
          <a-input v-model="form.address.zipcode" placeholder="Enter Zipcode" />
        </a-form-model-item>
      </div>

      <h3>Contacts</h3>

      <div class="flex flex-column gap-2">
        <a-form-model-item :colon="false" label-align="left" label="Email" prop="email">
          <a-input v-model="form.email" placeholder="Email" autocomplete="off" />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Phone" prop="phone">
          <phone-input
            @input="
              value => {
                $refs.form.validateField('phone')
                form.phone = value
              }
            "
            :value="form.phone"
          />
        </a-form-model-item>
      </div>

      <h3 class="m-0">Extra Settings</h3>
      <div class="flex flex-column gap-2">
        <a-form-model-item :colon="false" label-align="left" label="Description" prop="description">
          <a-textarea v-model="form.description" placeholder="Description" /> </a-form-model-item
        ><a-form-model-item :colon="false" label-align="left" label="Tags">
          <a-textarea v-model="form.tags" placeholder="Tags" />
        </a-form-model-item>

        <a-form-model-item
          :colon="false"
          label-align="left"
          label="What is the purpose of creating your company in ePeople?"
          prop="project_template"
          extra="This helps us tailor your workspace. After selecting a purpose, we will configure your project’s features to best fit your needs — adding the right sections, modules, and tools!"
        >
          <custom-select
            :multiple="false"
            :allow-clear="false"
            :value.sync="form.project_template"
            placeholder="Select your company’s purpose…"
            :options="projectTemplates"
            name="title"
            optionKey="key"
          />
        </a-form-model-item>
      </div>

      <h3>Available Features</h3>

      <div class="flex flex-column gap-2">
        <div class="grid">
          <div
            class="col-6 flex gap-2"
            v-for="feature in PROJECT_TEMPLATES[form.project_template].features"
            :key="feature"
          >
            <div
              class="flex align-items-center"
              :style="{
                borderRight: `2px solid ${getFeatures(feature).color}`
              }"
            >
              <a-tag :style="{ fontSize: '20px', padding: '7px' }" :color="getFeatures(feature).lightColor">
                <a-icon :type="getFeatures(feature).icon" :style="{ color: getFeatures(feature).color }" />
              </a-tag>
            </div>
            <div class="flex flex-column gap-2 text-left">
              <h4 class="m-0">{{ getFeatures(feature).title }}</h4>
              <p class="m-0">{{ getFeatures(feature).description }}</p>
            </div>
          </div>
        </div>
      </div>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button key="back" @click="closeModal">Cancel</a-button>
        <a-button key="submit" type="primary" @click="save" :loading="loading">Save</a-button>
      </div>
    </template></a-modal
  >
</template>

<script>
import CustomSelect from '@/components/ui/custom-select.vue'
import constData from '@/helpers/const-data'
import { debounce } from '@/helpers/debounce'
import { FEATURES_INFO, PROJECT_TEMPLATES } from '@/store/modules/project/constants'
import { Button, FormModel, Icon, Input, Modal, notification, Tag } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import UploadLogo from './upload-logo.vue'
import PhoneInput from '@/components/ui/phone-input.vue'
import { deepCopy } from '@/utils/deep-copy'

const { mapActions: legalEntityActions } = createNamespacedHelpers('legalEntity')
const { mapActions: projectActions, mapGetters: projectGetters } = createNamespacedHelpers('project')

const initialForm = {
  name: undefined,
  short_name: undefined,
  description: null,
  email: undefined,
  phone: undefined,
  tax_id: null,
  payroll_tax_id: null,
  join_type: undefined,
  tags: null,
  time_zone: undefined,
  project_template: 'all',
  address: {
    address_line1: null,
    address_line2: null,
    city: null,
    state: null,
    zipcode: null,
    country: null
  }
}

const initialRules = {
  name: [
    {
      required: true,
      message: 'Please enter the company name!'
    },
    {
      min: 2,
      max: 100,
      message: 'The name must be between 2 and 100 characters.'
    }
  ],
  short_name: [
    {
      required: true,
      message: 'Please enter the company short name!'
    },
    {
      min: 2,
      max: 12,
      message: 'The short name must be between 2 and 12 characters.'
    }
  ],
  email: [
    {
      required: true,
      message: 'Please enter the email address!'
    },
    {
      type: 'email',
      message: 'The input is not a valid email!'
    }
  ],
  phone: [
    {
      required: true,
      message: 'Phone can not be empty',
      trigger: 'change'
    },
    {
      transform: value => (value ? value.replace(/\s+/g, '') : value),
      pattern: /^\+\d{7,18}$/,
      message: 'Invalid phone number. It should start with a + and contain 10 to 15 digits.'
    }
  ],
  join_type: [
    {
      required: true,
      message: 'Please select a join type!'
    }
  ],
  address: {
    zipcode: [
      {
        min: 5,
        message: 'The zipcode must be more than 4 characters.',
        trigger: 'change'
      }
    ],
    country: [
      {
        required: true,
        message: 'Country can not be empty',
        trigger: 'change'
      }
    ]
  },
  project_template: [
    {
      required: true,
      message: 'Please select a purpose!'
    }
  ],
  time_zone: [
    {
      required: true,
      message: 'Please select a time zone!'
    }
  ]
}

export default {
  components: {
    'a-button': Button,
    'a-input': Input,
    'a-textarea': Input.TextArea,
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'custom-select': CustomSelect,
    'a-icon': Icon,
    'a-tag': Tag,
    'upload-logo': UploadLogo,
    'phone-input': PhoneInput
  },
  data() {
    return {
      loading: false,
      loadingTaxId: false,
      loadingProject: false,

      logo_file_id: undefined,

      form: {
        ...initialForm,
        phone: this.$store.state.applicationState?.associate?.phone ?? undefined,
        email: this.$store.state.applicationState?.associate?.email ?? undefined,
        join_type: 'request'
      },

      timeZones: moment.tz.names().map(item => ({ name: item, id: item })),

      rules: {
        ...initialRules,
        tax_id: [
          {
            len: 9,
            message: 'Tax ID must be contain 9 digit',
            trigger: 'change',
            transform: value => (value ? value.replace(/\D/g, '') : value)
          },
          {
            validator: this.taxIdValidator,
            trigger: 'change'
          }
        ]
      },

      joinTypesList: [
        {
          name: 'Open',
          id: 'open',
          extra:
            'Anyone can find your company and join without additional verification or an invitation. You can also invite anyone.'
        },
        {
          name: 'Request or Invite',
          id: 'request',
          extra:
            'Anyone can request to join your company. After you approve the request, you can accept them. You can also invite anyone to hire them.'
        },
        {
          name: 'Invite Only',
          id: 'invite',
          extra: 'Your company is hidden from search, but you can invite anyone to hire them.'
        }
      ],

      countriesList: constData.countries,
      statesList: constData.usStates,

      legalEntities: [],

      debouncedTaxIdValidator: null,

      projectTemplates: Object.entries(PROJECT_TEMPLATES).map(([key, value]) => ({ key, ...value })),

      FEATURES_INFO,
      PROJECT_TEMPLATES
    }
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    }
  },
  watch: {
    'form.address.country': {
      handler(newVal) {
        if (newVal && newVal.toUpperCase() !== 'US') {
          this.form.address.state = undefined
        }
      }
    },
    'form.tax_id': {
      handler: debounce(async function (newVal) {
        if (!newVal) {
          this.legalEntities = []
          return
        }

        this.loadingTaxId = true

        try {
          const result = await this.getLegalEntityByTaxId({
            tax_id: newVal.replace(/\D/g, '')
          })

          this.legalEntities = result
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loadingTaxId = false
        }
      }, 300)
    }
  },
  computed: {
    ...projectGetters(['getFeatures']),

    stateProxy: {
      get() {
        return this.form.address.state ? this.form.address.state.toUpperCase() : undefined
      },
      set(value) {
        this.form.address.state = value
      }
    },

    countryProxy: {
      get() {
        return this.form.address.country ? this.form.address.country.toUpperCase() : undefined
      },
      set(value) {
        this.form.address.country = value
      }
    },

    taxIdExtraText() {
      let connectedClients = 0
      let potentialClients = 0

      ;(this.legalEntities ?? []).forEach(item => {
        if (item.staffing_status === 'linked') {
          connectedClients += 1
        } else {
          potentialClients += 1
        }
      })

      return `Your company is connected with ${connectedClients} clients and has ${potentialClients} pending clients`
    }
  },
  methods: {
    ...legalEntityActions(['getLegalEntityByTaxId']),
    ...projectActions(['getProjectByTaxId', 'postCreateProject']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    taxIdValidator: debounce(async function (rule, value, callback) {
      try {
        if (!value) {
          return callback()
        }

        this.loadingProject = true

        const result = await this.getProjectByTaxId({
          tax_id: value.replace(/\D/g, '')
        })

        if (result) {
          callback(
            new Error(
              'The specified Tax ID is ALREADY in USE in one of your Company. Please double-check the information! After that, try again!'
            )
          )
        } else {
          return callback()
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingProject = false
      }
    }, 300),

    closeModal() {
      this.form = deepCopy({
        ...initialForm,
        phone: this.$store.state.applicationState?.associate?.phone ?? undefined,
        email: this.$store.state.applicationState?.associate?.email ?? undefined,
        join_type: 'request'
      })
      this.legalEntities = []
      this.logo_file_id = undefined
      this.$emit('update:visible', false)
    },

    save() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        this.loading = true

        try {
          const payload = {
            project: {
              ...this.form,
              tax_id: this.form.tax_id ? this.form.tax_id.replace(/\D/g, '') : undefined,
              logo_file_id: this.logo_file_id,
              email: this.form.email.toLowerCase()
            }
          }

          await this.postCreateProject(payload)

          this.showNotification(
            'success',
            'Continue Setting Up Your Company!',
            'Your company has been created successfully, and you can start using it right away! Our app has lots of other features you might want to explore and set up. Everything is at your fingertips! Enjoy!'
          )

          this.closeModal()
        } catch (error) {
          this.showNotification('error', 'Error', error?.message ?? error)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  flex-direction: column;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

h3 {
  margin: 0;
}

img {
  width: 128px;
  height: 128px;
  border-radius: 50%;
}

::v-deep .ant-upload-picture-card-wrapper {
  width: fit-content;
  align-self: center;
}

.avatar-uploader > .ant-upload {
  width: 128px;
  height: 128px;
}

.ant-upload-select-picture-card i {
  font-size: 18px;
  color: #999;
}

.ant-upload-select-picture-card .ant-upload-text {
  margin-top: 8px;
  color: #666;
}

::v-deep .ant-upload.ant-upload-select-picture-card > .ant-upload {
  padding: 0 !important;
}

::v-deep .ant-upload.ant-upload-select-picture-card {
  margin: 0 !important;
  border-radius: 50%;
  width: 128px;
  height: 128px;
}

::v-deep .ant-modal-body {
  max-height: 600px;
  overflow-y: auto;
}

::v-deep .ant-modal-content {
  overflow: hidden;
}
</style>
